import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button } from '@nexply/ui';
import { useAuth } from '../lib/auth.js';
import { FloatingField } from '../components/FloatingField.js';
import { LegalFooter } from '../components/LegalFooter.js';

/**
 * Wohin nach dem Anmelden — normalerweise die App, bei `?next=` zurück zur Seite, die
 * den Login angestoßen hat (etwa ein Einladungslink).
 *
 * Nur repo-interne Pfade sind erlaubt: `//example.com` wäre für den Browser eine fremde
 * Domain und damit eine offene Weiterleitung.
 */
export function nextTarget(): string {
  const raw = new URLSearchParams(window.location.search).get('next');
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/app';
  return raw;
}

/** `?next=` beim Wechsel zwischen Login und Registrierung mitnehmen. */
export function nextSearch(): { next?: string } {
  const t = nextTarget();
  return t === '/app' ? {} : { next: t };
}

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      await nav({ to: nextTarget() });
    } catch (err) {
      const e = err as { code?: string; message?: string };
      setError(
        e.code === 'invalid_credentials'
          ? 'E-Mail oder Passwort ist falsch.'
          : e.message ?? 'Anmeldung fehlgeschlagen.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-ink lg:grid lg:grid-cols-[1.2fr_1fr] xl:grid-cols-[1.4fr_1fr]">
      <BrandSide />

      <div className="relative z-10 flex min-h-dvh w-full flex-col lg:min-h-dvh">
        {/* Mobile background glow */}
        <div className="lg:hidden pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[420px] w-[420px] rounded-full bg-gold/15 blur-[120px]" />
        <div className="lg:hidden pointer-events-none absolute bottom-0 right-0 h-[280px] w-[280px] rounded-full bg-gold/10 blur-[100px]" />

        <div className="relative z-10 mx-auto flex w-full max-w-sm flex-1 flex-col px-6 pt-[calc(env(safe-area-inset-top)+48px)] pb-[calc(env(safe-area-inset-bottom)+24px)] lg:max-w-md lg:justify-center lg:py-12 lg:px-10">
          <div className="text-center mb-10 lg:hidden animate-slide-up">
            <h1 className="text-6xl font-semibold tracking-tighter text-gold-grad">nexply</h1>
            <p className="mt-3 text-[11px] uppercase tracking-[0.4em] text-gold/70">Life is a Puzzle</p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4 animate-slide-up lg:flex-none lg:rounded-3xl lg:border lg:border-line lg:bg-surface-1 lg:p-8 lg:shadow-card-lg">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-text">Willkommen zurück</h2>
              <p className="mt-1 text-sm text-text-dim">Melde dich mit deinem Account an.</p>
            </div>

            <div className="space-y-3 mt-2">
              <FloatingField
                id="email"
                label="E-Mail"
                type="email"
                autoComplete="email"
                value={email}
                onChange={setEmail}
                required
              />
              <FloatingField
                id="password"
                label="Passwort"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                required
              />
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-text-dim hover:text-gold-light transition"
                >
                  Passwort vergessen?
                </Link>
              </div>
            </div>

            {error && (
              <div className="text-xs text-danger bg-danger/10 border border-danger/25 rounded-xl px-4 py-3">
                {error}
              </div>
            )}

            <Button type="submit" size="lg" fullWidth loading={busy}>
              Anmelden
            </Button>

            <div className="mt-auto lg:mt-2 pt-8 lg:pt-4 text-center text-sm text-text-dim">
              Noch kein Account?{' '}
              <Link to="/register" search={nextSearch()} className="font-medium text-gold-light hover:text-gold transition">
                Konto anlegen
              </Link>
            </div>
          </form>
          <LegalFooter className="mt-8 lg:mt-6" />
        </div>
      </div>
    </div>
  );
}

export function BrandSide() {
  return (
    <div className="hidden lg:flex relative overflow-hidden bg-ink-2">
      <div className="pointer-events-none absolute -top-40 -left-40 h-[640px] w-[640px] rounded-full bg-gold/20 blur-[160px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-gold-dark/30 blur-[140px]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #e8c988 0px, transparent 2px), radial-gradient(circle at 70% 60%, #d4af66 0px, transparent 2px), radial-gradient(circle at 40% 80%, #9a7e3a 0px, transparent 2px)',
          backgroundSize: '64px 64px, 96px 96px, 80px 80px',
        }}
      />
      <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
        <div className="flex items-center gap-2 text-text-dim">
          <div className="h-2 w-2 rounded-full bg-gold animate-pulse" />
          <span className="text-[11px] uppercase tracking-[0.4em] text-gold/80">Network OS</span>
        </div>
        <div>
          <h1 className="text-7xl xl:text-8xl font-semibold tracking-tighter text-gold-grad leading-[0.95]">
            nexply
          </h1>
          <p className="mt-4 text-sm xl:text-base uppercase tracking-[0.4em] text-gold/70">Life is a Puzzle</p>
          <p className="mt-10 max-w-md text-base xl:text-lg text-text-dim leading-relaxed">
            Verbinde Menschen, schließe Deals, baue dein Netzwerk strategisch aus – mit einem Tool, das so denkt wie du.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-text-muted">
          © {new Date().getFullYear()} · nexply
        </div>
      </div>
    </div>
  );
}
